import React, { useEffect, useState, useCallback } from "react";
import { Lightbulb, X, Send, CheckCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCountry } from "../lib/country";
import { useLanguage } from "../lib/language";
import { toast } from "sonner";

/**
 * Floating banner + popup modal for the monthly suggestion campaign.
 * - Banner: always visible at top of site when campaign active (dismissible per session).
 * - Popup: shown once per (user, month) automatically when active. Manual trigger via banner button.
 * - On submit: $X credit goes to user's wallet_balance, banner disappears for them.
 */
export default function SuggestionCampaign() {
    const { user, refresh } = useAuth();
    const { country } = useCountry();
    const { lang, t } = useLanguage();
    const [cfg, setCfg] = useState(null);
    const [bannerHidden, setBannerHidden] = useState(false);
    const [popupOpen, setPopupOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState("");
    const [submitted, setSubmitted] = useState(false);

    // Pick localized strings from the campaign config (EN fields are populated by the backend
    // whenever the admin edits the ES text). Falls back to ES if EN empty.
    const displayTitle = lang === "en" && cfg?.title_en ? cfg.title_en : cfg?.title;
    const displayMessage = lang === "en" && cfg?.message_en ? cfg.message_en : cfg?.message;
    const displayButton = lang === "en" && cfg?.button_text_en ? cfg.button_text_en : cfg?.button_text;

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/suggestion-campaign");
            setCfg(data);
        } catch {
            setCfg(null);
        }
    }, []);

    useEffect(() => { load(); }, [load, user?.id]);

    // Auto popup once per (user, month, session) when active & not yet participated
    useEffect(() => {
        if (!cfg || !cfg.active) return;
        if (!user) return; // require login
        if (cfg.participated) return;
        const key = `tb_suggestion_popup_${cfg.month}_${user.id}`;
        if (sessionStorage.getItem(key)) return;
        const tid = setTimeout(() => {
            setPopupOpen(true);
            sessionStorage.setItem(key, "1");
        }, 4000);
        return () => clearTimeout(tid);
    }, [cfg, user]);

    if (!cfg || !cfg.active) return null;

    const submit = async () => {
        const msg = message.trim();
        if (msg.length < 10) { toast.error("Tu sugerencia debe tener al menos 10 caracteres"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/suggestions", { message: msg, country });
            setSubmitted(true);
            toast.success(`¡Gracias! Recibiste $${(data.credit_amount || 0).toFixed(2)} de crédito en tu cuenta`);
            try { await refresh?.(); } catch { /* ignore */ }
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo enviar la sugerencia");
        } finally { setSubmitting(false); }
    };

    const close = () => { setPopupOpen(false); setMessage(""); setSubmitted(false); };
    const openManual = () => {
        if (!user) {
            toast.message("Inicia sesión para participar y recibir tu crédito", { duration: 4000 });
            return;
        }
        if (cfg.participated) {
            toast.success(`Ya participaste en la campaña de ${cfg.month}. ¡Gracias!`);
            return;
        }
        setPopupOpen(true);
    };

    const showBanner = !bannerHidden && (!user || !cfg.participated);

    return (
        <>
            {showBanner && (
                <div className="bg-gradient-to-r from-[var(--tb-gold)]/15 to-[var(--tb-border-gold)]/10 border-b border-[var(--tb-border-gold)]/30"
                    data-testid="suggestion-banner">
                    <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3 text-xs sm:text-sm">
                        <Lightbulb className="w-4 h-4 text-[var(--tb-gold)] shrink-0" />
                        <div className="flex-1 truncate">
                            <span className="text-[var(--tb-text)]">{displayTitle}</span>
                        </div>
                        <button onClick={openManual}
                            className="tb-btn-primary text-xs whitespace-nowrap inline-flex items-center gap-1 px-3 py-1"
                            data-testid="suggestion-banner-cta">
                            {displayButton}
                        </button>
                        <button onClick={() => setBannerHidden(true)}
                            className="text-[var(--tb-muted)] hover:text-[var(--tb-text)] transition"
                            aria-label={t("Cerrar", "Close")}
                            data-testid="suggestion-banner-close">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {popupOpen && (
                <div className="fixed inset-0 z-[100000] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    onClick={close} data-testid="suggestion-modal-backdrop">
                    <div className="bg-[var(--tb-bg)] border border-[var(--tb-border-gold)]/40 rounded-lg max-w-md w-full p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()} data-testid="suggestion-modal">
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-full bg-[var(--tb-border-gold)]/20 flex items-center justify-center">
                                    <Lightbulb className="w-5 h-5 text-[var(--tb-gold)]" />
                                </div>
                                <div>
                                    <h3 className="font-display text-lg leading-tight">{displayTitle}</h3>
                                    <p className="text-[10px] text-[var(--tb-muted)]">{t("Campaña", "Campaign")} {cfg.month}</p>
                                </div>
                            </div>
                            <button onClick={close} className="text-[var(--tb-muted)] hover:text-[var(--tb-text)]"
                                data-testid="suggestion-modal-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        {submitted ? (
                            <div className="text-center py-4 space-y-3" data-testid="suggestion-submitted">
                                <CheckCircle className="w-12 h-12 text-[var(--tb-gold)] mx-auto" />
                                <p className="text-sm">{t("¡Gracias por tu aporte! Tu crédito ya está disponible en tu cuenta.", "Thanks for your input! Your credit is now available in your account.")}</p>
                                <button onClick={close} className="tb-btn-primary text-sm" data-testid="suggestion-modal-done">{t("Cerrar", "Close")}</button>
                            </div>
                        ) : (
                            <>
                                <p className="text-xs text-[var(--tb-text-2)] mb-3">{displayMessage}</p>
                                <textarea className="tb-input w-full min-h-[120px] mb-3"
                                    placeholder={t("Cuéntanos qué te gustaría mejorar en Terra Boutique...", "Tell us what you'd like to improve about Terra Boutique...")}
                                    value={message} onChange={(e) => setMessage(e.target.value)}
                                    maxLength={2000}
                                    data-testid="suggestion-modal-textarea" />
                                <div className="flex items-center justify-between text-[10px] text-[var(--tb-muted)] mb-3">
                                    <span>{message.length}/2000 {t("caracteres", "characters")}</span>
                                    <span>{t("Recibirás", "You'll receive")} ${(cfg.credit_amount || 1).toFixed(2)} {t("en tu saldo", "in your balance")}</span>
                                </div>
                                <button onClick={submit} disabled={submitting}
                                    className="tb-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                    data-testid="suggestion-modal-submit">
                                    <Send className="w-4 h-4" /> {submitting ? t("Enviando...", "Sending...") : (displayButton || t("Enviar sugerencia", "Send suggestion"))}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
